// STR (Smith Travel Research) hotel/motel segment benchmarks.
//
// Typical 2025-Q4 / 2026-Q1 RevPAR ranges per US hospitality segment, plus
// the segment's price-elasticity-of-demand coefficient. Lower-end segments
// price-shop hard (more elastic, e ≈ -0.8); luxury guests are loyal and
// brand-driven (less elastic, e ≈ -0.4). These are baked into the per-row
// "raise ADR by X%, expect occupancy ~Y%" caveat the dashboard surfaces.
//
// Source: industry-typical ranges (STR / HVS Hospitality Briefs); see
// https://str.com/data-insights/segment-mix for the official segmentation.
//
// Single source of truth — consumed by:
//   - Properties.jsx (hospitality form dropdown + row badge)
//   - any future hospitality reports / dashboards
export const STR_SEGMENTS = [
  { value: "economy",        label: "Economy",        revparMin: 45,  revparMax: 65,  elasticity: -0.8,  description: "Budget chains (Super 8, Motel 6)" },
  { value: "midscale",       label: "Midscale",       revparMin: 70,  revparMax: 95,  elasticity: -0.7,  description: "Limited-service (Holiday Inn Express, La Quinta)" },
  { value: "upper_midscale", label: "Upper Midscale", revparMin: 95,  revparMax: 125, elasticity: -0.65, description: "Hampton, Best Western Plus, Comfort Suites" },
  { value: "upscale",        label: "Upscale",        revparMin: 125, revparMax: 165, elasticity: -0.6,  description: "Courtyard, Hilton Garden, Hyatt Place" },
  { value: "upper_upscale",  label: "Upper Upscale",  revparMin: 165, revparMax: 220, elasticity: -0.5,  description: "Marriott, Hilton, Hyatt Regency" },
  { value: "luxury",         label: "Luxury",         revparMin: 220, revparMax: 380, elasticity: -0.4,  description: "Ritz-Carlton, Four Seasons, St. Regis" },
];

export const STR_BY_VALUE = Object.fromEntries(STR_SEGMENTS.map((s) => [s.value, s]));

// Industry-typical price elasticity of demand for hotels (Q4 2025 ARR meta-analysis):
// a 1% ADR increase typically yields ~0.6% occupancy decrease in the mid-market.
// Per-segment refinement is now baked into STR_SEGMENTS (luxury -0.4 → economy -0.8);
// this constant remains as the fallback for hotels with no declared segment.
export const DEFAULT_PRICE_ELASTICITY = -0.6;

/**
 * Look up the per-segment elasticity coefficient. Falls back to the mid-market
 * default when the segment is unknown (or null/undefined).
 */
export function elasticityForSegment(segment) {
  const s = STR_BY_VALUE[segment];
  return (s && typeof s.elasticity === "number") ? s.elasticity : DEFAULT_PRICE_ELASTICITY;
}

/**
 * Project the post-elasticity outcome of raising/lowering ADR.
 * Useful caveat for "raise ADR to hit segment midpoint" suggestions because
 * a naive midpoint solve ignores that demand will fall as price rises.
 *
 * The elasticity coefficient defaults to the mid-market value but callers
 * SHOULD pass a segment-specific value (via `elasticityForSegment`) when
 * the hotel has a declared STR segment — luxury demand barely budges with
 * ADR moves, while economy guests evaporate at the first price increase.
 *
 * Returns null when inputs are missing or non-positive. Otherwise:
 *   { projectedOcc, occDropPct, projectedRevpar, revparGain, netPositive, elasticity }
 */
export function projectElasticity(currentAdr, newAdr, currentOccupancy, elasticity = DEFAULT_PRICE_ELASTICITY) {
  const c = Number(currentAdr);
  const n = Number(newAdr);
  const o = Number(currentOccupancy);
  if (!(c > 0) || !(n > 0) || !(o > 0)) return null;
  const adrChangePct = (n - c) / c;
  // Cap the demand multiplier to [0, 1.5] so silly extreme inputs (e.g. 5x ADR)
  // don't produce negative occupancy or absurd elastic uplift.
  const occMult = Math.max(0, Math.min(1.5, 1 + elasticity * adrChangePct));
  const projectedOcc = o * occMult;
  const projectedRevpar = n * (projectedOcc / 100);
  const currentRevpar = c * (o / 100);
  return {
    projectedOcc: Math.round(projectedOcc * 10) / 10,
    occDropPct: Math.round((projectedOcc - o) * 10) / 10, // signed: negative when ADR went up
    projectedRevpar: Math.round(projectedRevpar * 100) / 100,
    revparGain: Math.round((projectedRevpar - currentRevpar) * 100) / 100,
    netPositive: projectedRevpar > currentRevpar,
    elasticity,
  };
}

/**
 * Compare a hotel's current RevPAR against its declared segment.
 * Returns { status: "below" | "in_range" | "above" | "unknown", min, max, label, gap_pct }
 * where gap_pct is signed: negative = under-performing midpoint, positive = beating it.
 */
export function strBenchmarkFor(segment, revpar) {
  const s = STR_BY_VALUE[segment];
  if (!s) return { status: "unknown" };
  if (typeof revpar !== "number" || revpar <= 0) {
    return { status: "unknown", min: s.revparMin, max: s.revparMax, label: s.label };
  }
  const mid = (s.revparMin + s.revparMax) / 2;
  const gapPct = ((revpar - mid) / mid) * 100;
  let status = "in_range";
  if (revpar < s.revparMin) status = "below";
  else if (revpar > s.revparMax) status = "above";
  return {
    status,
    min: s.revparMin,
    max: s.revparMax,
    label: s.label,
    mid,
    gap_pct: Math.round(gapPct * 10) / 10,
  };
}
